import { Injectable }       from '@angular/core';
import { NgbModal }         from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { ApiService }                   from '../api/api.service';
import { CompanyIntegrationsModel }     from '../companies/models/company.integrations';
import { ConfirmService }               from '../messages/confirm/confirm.service';
import { CustomerModel }                from '../customers/models/customer';
import { VehicleModel }                 from '../vehicles/models/vehicle';
import { VVGarageAddCustomerComponent } from './vv-garage.add-customer.component';
import { VVGarageCustomerModel }        from './typings/vv-garage.customer';
import { VVGarageLocationModel }        from './typings/vv-garage.location';

@Injectable({
  providedIn: 'root'
})
export class VVGarageService {
  public title = this.translateService.instant('VV_GARAGE_SPACE.VV_GARAGE');

  constructor (
    private apiService      : ApiService,
    private confirmService  : ConfirmService,
    private modalService    : NgbModal,
    private translateService: TranslateService
  ) {}

  activate () : Promise<any> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.ACTIVATE', {
        name: this.title
      }))
      .then(() => this.apiService.get('/company_integration/add_vv_garage'));
  }

  addCustomer ( customer : CustomerModel ) : Promise<any> {
    const modal = this.modalService.open(VVGarageAddCustomerComponent);

    modal.componentInstance.customer = this.getCustomer(customer);

    return modal.result;
  }

  addVehicleToCustomer ( customer : CustomerModel, vehicle : VehicleModel ) : Promise<any> {
    return this.apiService.post('/vv_garage/add_vehicle_to_customer', {
        vehicle  : vehicle,
        vv_garage: this.getCustomer(customer)
      }, 'vv_garage');
  }

  createCustomer ( customer : VVGarageCustomerModel ) : Promise<any> {
    return this.apiService.post('/vv_garage/add_customer', customer, 'vv_garage');
  }

  deactivate () : Promise<any> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.REMOVE', {
        name: this.title
      }))
      .then(() => this.apiService.get('/company_integration/remove_vv_garage'));
  }

  deleteCustomer ( customer : CustomerModel ) : Promise<any> {
    return this.apiService.post('/vv_garage/delete_customer', this.getCustomer(customer), 'vv_garage');
  }

  deleteCustomerVehicle ( customer : CustomerModel, vehicle : VehicleModel ) : Promise<any> {
    return this.apiService.post('/vv_garage/delete_customer_vehicle', {
        vehicle  : vehicle,
        vv_garage: this.getCustomer(customer)
      }, 'vv_garage');
  }

  getIntegrations () : Promise<CompanyIntegrationsModel> {
    return this.apiService.get('/company_integrations');
  }

  getCustomer ( customer : CustomerModel ) : VVGarageCustomerModel {
    const firstName = customer.first_name || customer.contact_first;
    const lastName  = customer.last_name  || customer.contact_last;
    const email     = customer.email      || customer.contact_email;
    const mobile    = customer.mobile     || '';

    return {
      customer_id          : customer.id,
      email                : email     || '',
      first_name           : firstName || '',
      last_name            : lastName  || '',
      mobile               : mobile    || '',
      username             : email     || '',
      vv_garage_customer_id: customer.vv_garage_customer_id
    };
  }

  getLocations () : Promise<VVGarageLocationModel> {
    return this.apiService.get('/vv_garage/get_location_list')
      .then(locations => locations
        ? locations
        : []
      );
  }

  save ( settings : CompanyIntegrationsModel ) : Promise<CompanyIntegrationsModel> {
    return this.apiService.patch('/company_integrations', settings, 'company_integration');
  }
}
